import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';
import { Decoration } from '@tiptap/pm/view';
import { findReplacePluginKey } from '../../extensions/FindReplaceExtension';

interface FindReplaceProps {
  editor: Editor;
  onClose: () => void;
}

interface SearchMatch {
  from: number;
  to: number;
}

function findMatches(editor: Editor, searchTerm: string, caseSensitive: boolean): SearchMatch[] {
  if (!searchTerm) return [];

  const matches: SearchMatch[] = [];
  const doc = editor.state.doc;
  const searchLower = caseSensitive ? searchTerm : searchTerm.toLowerCase();

  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;
    const text = caseSensitive ? node.text : node.text.toLowerCase();
    let index = 0;

    while (index < text.length) {
      const found = text.indexOf(searchLower, index);
      if (found === -1) break;
      matches.push({
        from: pos + found,
        to: pos + found + searchTerm.length,
      });
      index = found + 1;
    }
  });

  return matches;
}

export function FindReplace({ editor, onClose }: FindReplaceProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [replaceTerm, setReplaceTerm] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [showReplace, setShowReplace] = useState(false);
  const [matches, setMatches] = useState<SearchMatch[]>([]);
  const [currentMatch, setCurrentMatch] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search input on mount
  useEffect(() => {
    searchInputRef.current?.focus();
    // If there's selected text, use it as the initial search term
    const { from, to } = editor.state.selection;
    if (from !== to) {
      const text = editor.state.doc.textBetween(from, to);
      if (text.length < 100) setSearchTerm(text);
    }
  }, []);

  const runSearch = useCallback(() => {
    const foundMatches = findMatches(editor, searchTerm, caseSensitive);
    setMatches(foundMatches);
    const idx = foundMatches.length > 0 ? Math.min(currentMatch, foundMatches.length - 1) : 0;
    if (foundMatches.length === 0) setCurrentMatch(0);
    else setCurrentMatch(prev => Math.min(prev, foundMatches.length - 1));
    updateDecorations(editor, foundMatches, foundMatches.length > 0 ? idx : -1);
  }, [editor, searchTerm, caseSensitive, currentMatch, updateDecorations]);

  // Search and update decorations
  useEffect(() => {
    runSearch();
  }, [searchTerm, caseSensitive, editor]);

  // Update decoration highlighting when current match changes
  useEffect(() => {
    if (matches.length > 0) {
      updateDecorations(editor, matches, currentMatch);
      // Scroll current match into view
      const match = matches[currentMatch];
      if (match) {
        editor.commands.setTextSelection(match.from);
        const dom = editor.view.domAtPos(match.from);
        if (dom?.node) {
          const el = dom.node instanceof HTMLElement ? dom.node : dom.node.parentElement;
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  }, [currentMatch, matches]);

  const updateDecorations = useCallback((editor: Editor, matches: SearchMatch[], activeIndex: number) => {
    const decorations = matches.map((match, i) => {
      const className = i === activeIndex ? 'find-match-active' : 'find-match';
      return Decoration.inline(match.from, match.to, { class: className });
    });

    editor.view.dispatch(
      editor.state.tr.setMeta(findReplacePluginKey, decorations)
    );
  }, []);

  const goToNext = useCallback(() => {
    if (matches.length === 0) return;
    setCurrentMatch(prev => (prev + 1) % matches.length);
  }, [matches]);

  const goToPrev = useCallback(() => {
    if (matches.length === 0) return;
    setCurrentMatch(prev => (prev - 1 + matches.length) % matches.length);
  }, [matches]);

  const handleReplace = useCallback(() => {
    if (matches.length === 0) return;
    const match = matches[currentMatch];
    if (!match) return;

    editor.chain()
      .focus()
      .insertContentAt({ from: match.from, to: match.to }, replaceTerm)
      .run();

    // Rerun search after replace
    const newMatches = findMatches(editor, searchTerm, caseSensitive);
    setMatches(newMatches);
    setCurrentMatch(prev => Math.min(prev, Math.max(0, newMatches.length - 1)));
    updateDecorations(editor, newMatches, Math.min(currentMatch, Math.max(0, newMatches.length - 1)));
  }, [editor, matches, currentMatch, replaceTerm, searchTerm, caseSensitive]);

  const handleReplaceAll = useCallback(() => {
    if (matches.length === 0) return;

    // Replace from end to start to preserve positions
    const sorted = [...matches].sort((a, b) => b.from - a.from);
    let chain = editor.chain().focus();

    for (const match of sorted) {
      chain = chain.insertContentAt({ from: match.from, to: match.to }, replaceTerm);
    }

    chain.run();
    setMatches([]);
    setCurrentMatch(0);
    updateDecorations(editor, [], -1);
  }, [editor, matches, replaceTerm]);

  const handleClose = useCallback(() => {
    // Clear decorations
    updateDecorations(editor, [], -1);
    onClose();
  }, [editor, onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      goToNext();
    } else if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      goToPrev();
    }
  }, [handleClose, goToNext, goToPrev]);

  return (
    <div className="fixed top-12 right-4 z-50 bg-white dark:bg-ink-800 border border-ink-200 dark:border-ink-700 rounded-lg shadow-lg p-3 min-w-[320px] animate-fade-in">
      {/* Search row */}
      <div className="flex items-center gap-2">
        <input
          ref={searchInputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Find..."
          className="flex-1 text-sm bg-ink-50 dark:bg-ink-900 border border-ink-200 dark:border-ink-700 rounded px-2 py-1 outline-none focus:border-blue-400 dark:focus:border-blue-500 text-ink-800 dark:text-ink-200 placeholder:text-ink-400 dark:placeholder:text-ink-600"
        />
        <span className="text-[11px] text-ink-400 dark:text-ink-500 whitespace-nowrap min-w-[60px] text-center">
          {matches.length > 0 ? `${currentMatch + 1} of ${matches.length}` : 'No results'}
        </span>
        <button
          onClick={runSearch}
          className="text-ink-400 dark:text-ink-500 hover:text-ink-700 dark:hover:text-ink-300 p-1 rounded hover:bg-ink-100 dark:hover:bg-ink-700 transition-colors"
          title="Refresh search results"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M1.5 7a5.5 5.5 0 0 1 9.4-3.9M12.5 7a5.5 5.5 0 0 1-9.4 3.9" /><path d="M11 1v2.5h-2.5M3 11v-2.5h2.5" /></svg>
        </button>
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-1 mt-2">
        <button
          onClick={() => setCaseSensitive(!caseSensitive)}
          className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
            caseSensitive
              ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400'
              : 'text-ink-400 dark:text-ink-500 hover:bg-ink-100 dark:hover:bg-ink-700'
          }`}
          title="Case sensitive"
        >
          Aa
        </button>
        <div className="flex-1" />
        <button onClick={goToPrev} className="text-ink-400 dark:text-ink-500 hover:text-ink-700 dark:hover:text-ink-300 p-1 rounded hover:bg-ink-100 dark:hover:bg-ink-700 transition-colors" title="Previous (Shift+Enter)">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M7 11V3M3 7l4-4 4 4" /></svg>
        </button>
        <button onClick={goToNext} className="text-ink-400 dark:text-ink-500 hover:text-ink-700 dark:hover:text-ink-300 p-1 rounded hover:bg-ink-100 dark:hover:bg-ink-700 transition-colors" title="Next (Enter)">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M7 3v8M3 7l4 4 4-4" /></svg>
        </button>
        <button
          onClick={() => setShowReplace(!showReplace)}
          className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
            showReplace
              ? 'bg-ink-200 dark:bg-ink-700 text-ink-700 dark:text-ink-300'
              : 'text-ink-400 dark:text-ink-500 hover:bg-ink-100 dark:hover:bg-ink-700'
          }`}
        >
          Replace
        </button>
        <button onClick={handleClose} className="text-ink-400 dark:text-ink-500 hover:text-ink-700 dark:hover:text-ink-300 p-1 rounded hover:bg-ink-100 dark:hover:bg-ink-700 transition-colors" title="Close (Esc)">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M4 4l6 6M10 4l-6 6" /></svg>
        </button>
      </div>

      {/* Replace row */}
      {showReplace && (
        <div className="flex items-center gap-2 mt-2">
          <input
            type="text"
            value={replaceTerm}
            onChange={(e) => setReplaceTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Replace with..."
            className="flex-1 text-sm bg-ink-50 dark:bg-ink-900 border border-ink-200 dark:border-ink-700 rounded px-2 py-1 outline-none focus:border-blue-400 dark:focus:border-blue-500 text-ink-800 dark:text-ink-200 placeholder:text-ink-400 dark:placeholder:text-ink-600"
          />
          <button
            onClick={handleReplace}
            disabled={matches.length === 0}
            className="text-[11px] px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Replace
          </button>
          <button
            onClick={handleReplaceAll}
            disabled={matches.length === 0}
            className="text-[11px] px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            All
          </button>
        </div>
      )}
    </div>
  );
}
